import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types/enums';
import { useMemo } from 'react';

export interface Facility {
  id: string;
  name: string;
  [key: string]: any;
}

export interface User {
  uid: string;
  email: string;
  name: string;
  defaultFacility: Facility | null;
  roles: UserRole[];
  role: string;
}

export interface UserData {
  user: User | null;
  defaultFacility: Facility | null;
  userRoles: UserRole[];
  uid: string | null;
  email: string | null;
  name: string | null;
  isAdmin: boolean;
  isFacilityAdmin: boolean;
  isBillingSpecialist: boolean;
  hasBillingRole: boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

/**
 * Single source of truth for user data
 * Provides a clean interface for accessing user information and role checking
 * Uses useMemo to prevent unnecessary re-renders
 */
export const useUserData = (): UserData => {
  const { user } = useAuth();
  
  return useMemo(() => {
    if (!user) {
      return {
        user: null,
        defaultFacility: null,
        userRoles: [],
        uid: null,
        email: null,
        name: null,
        isAdmin: false,
        isFacilityAdmin: false,
        isBillingSpecialist: false,
        hasBillingRole: false,
        hasRole: () => false,
        hasAnyRole: () => false,
      };
    }

    // Get user roles using the new role system
    const userRoles = (user as User).roles || [(user as User).role].filter(Boolean);
    
    return {
      user,
      defaultFacility: (user as User).defaultFacility, // Always the complete facility object
      userRoles,
      uid: (user as User).uid,
      email: (user as User).email,
      name: (user as User).name,
      isAdmin: userRoles.includes(UserRole.ADMIN),
      isFacilityAdmin: userRoles.includes(UserRole.FACILITY_ADMIN),
      isBillingSpecialist: userRoles.includes(UserRole.BILLING_SPECIALIST),
      hasBillingRole: userRoles.includes(UserRole.BILLING_SPECIALIST) || userRoles.includes('billing' as any),
      hasRole: (role: string) => userRoles.includes(role as any),
      hasAnyRole: (roles: string[]) => roles.some(role => userRoles.includes(role as any)),
    };
  }, [user]);
};

export default useUserData;
